import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { Star, Quote, ChevronRight, Play } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import ReviewWidget from "@/components/ReviewWidget";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const videoTestimonials = [
  {
    id: "1",
    title: "Derek's Decompression Journey",
    videoUrl: "https://assets.cdn.filesafe.space/7UxsnzcuJDZvyZp0wk8z/media/69c6d639dc35ac17a9ea3680.mov",
    name: "Derek K.",
    service: "DRX9000 Decompression",
    category: "decompression",
    aspectRatio: "aspect-[9/16]",
    videoClass: "object-[center_20%]"
  },
  { 
    id: "2", 
    title: "Jim's SoftWave Success Story", 
    videoUrl: "https://assets.cdn.filesafe.space/7UxsnzcuJDZvyZp0wk8z/media/69c6d2f22c2b10821cddbd78.mov",
    name: "Jim C.",
    service: "SoftWave TRT",
    category: "softwave",
    aspectRatio: "aspect-[9/16]"
  },
  { 
    id: "3", 
    title: "ChiroThin Weight Loss Journey", 
    thumbnail: "https://vibe.filesafe.space/1774569058151009111/assets/0d77464e-f429-4205-8d33-067d8e054ca7.png",
    name: "Emily R.",
    service: "ChiroThin",
    category: "chirothin",
    aspectRatio: "aspect-[9/16]"
  }
];
import { SEOHead } from "@/components/SEOHead";

export default function Testimonials() {
  return (
    <div className="min-h-screen bg-background font-sans text-foreground flex flex-col">
      <SEOHead 
        title="Patient Testimonials | Woods Spine & Wellness | Longview TX"
        description="Read real reviews and watch success stories from patients who found relief at Woods Spine & Wellness in Longview, TX."
        ogImage="https://vibe.filesafe.space/1774569058151009111/assets/86591e09-33f7-4d49-908f-6be2e3f35732.png"
        canonical="https://woodsspine.com/testimonials"
      />
      <Navbar />
      <main className="flex-grow">
        {/* Hero Section */}
        <section className="relative pt-24 pb-20 lg:pt-32 lg:pb-32 overflow-hidden bg-primary text-primary-foreground">
          <div className="absolute inset-0 z-0 opacity-30">
            <img 
              src="https://wsrv.nl/?url=https://vibe.filesafe.space/1774569058151009111/assets/86591e09-33f7-4d49-908f-6be2e3f35732.png&w=1920&output=webp&q=80" 
              alt="Happy active patients enjoying life outdoors" 
              className="w-full h-full object-cover object-center"
              fetchPriority="high"
              decoding="async"
            />
            <div className="absolute inset-0 bg-primary/80 mix-blend-multiply"></div>
          </div>
          <div className="container relative z-10 mx-auto px-4 text-center max-w-4xl">
            <div className="inline-flex items-center justify-center mb-6">
              <div className="flex gap-1 text-yellow-400">
                <Star className="w-6 h-6 fill-current" />
                <Star className="w-6 h-6 fill-current" />
                <Star className="w-6 h-6 fill-current" />
                <Star className="w-6 h-6 fill-current" />
                <Star className="w-6 h-6 fill-current" />
              </div>
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-6">
              Real Patients.<br />
              <span className="text-accent">Real Results.</span>
            </h1>
            <p className="text-lg md:text-xl mb-8 text-primary-foreground/90 max-w-2xl mx-auto">
              Discover how our advanced, non-invasive therapies have helped our patients regain their mobility, eliminate pain, and get back to living life to the fullest.
            </p>
          </div>
        </section>

        {/* Featured Testimonial */}
        <section className="py-16 bg-background -mt-16 relative z-20">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto bg-card rounded-2xl shadow-xl p-8 md:p-12 border border-border relative overflow-hidden">
              <Quote className="absolute top-6 left-6 w-24 h-24 text-primary/5 -z-10 rotate-180" />
              <div className="relative z-10 text-center">
                <p className="text-xl md:text-2xl font-medium italic text-foreground/90 mb-8 leading-relaxed">
                  "After suffering from severe sciatica for years, I was told surgery was my only option. I found Woods Spine & Wellness and tried their DRX9000 decompression therapy. Within weeks, my pain was almost completely gone. I have my life back!"
                </p>
                <div className="flex items-center justify-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold text-lg">
                    MS
                  </div>
                  <div className="text-left">
                    <div className="font-bold text-foreground">Michael S.</div>
                    <div className="text-sm text-muted-foreground">Longview, TX</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Video Testimonials */}
        <section className="py-16 bg-muted/30">
          <div className="container mx-auto px-4">
            <div className="text-center max-w-3xl mx-auto mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-primary mb-4">Watch Their Stories</h2>
              <p className="text-lg text-muted-foreground">
                Hear directly from our patients about their experiences and life-changing results.
              </p>
            </div>
            
            <Tabs defaultValue="all" className="max-w-6xl mx-auto">
              <div className="flex justify-center mb-8">
                <TabsList className="bg-background/50 border border-border">
                  <TabsTrigger value="all">All</TabsTrigger>
                  <TabsTrigger value="decompression">Decompression</TabsTrigger>
                  <TabsTrigger value="softwave">SoftWave</TabsTrigger>
                  <TabsTrigger value="chirothin">ChiroThin</TabsTrigger>
                </TabsList>
              </div>

              {["all", "decompression", "softwave", "chirothin"].map((tab) => {
                const filteredVideos = videoTestimonials.filter((v) => tab === "all" || v.category === tab);
                return (
                <TabsContent key={tab} value={tab} className="mt-0 focus-visible:outline-none focus-visible:ring-0">
                  <div className={`grid gap-8 mx-auto ${
                    filteredVideos.length === 1 
                      ? 'grid-cols-1 max-w-sm' 
                      : filteredVideos.length === 2 
                        ? 'md:grid-cols-2 max-w-3xl' 
                        : 'md:grid-cols-2 lg:grid-cols-3'
                  }`}>
                    {filteredVideos.map((video) => (
                      <div key={video.id} className="group flex flex-col">
                        <div className={`relative rounded-2xl overflow-hidden ${video.aspectRatio || 'aspect-[9/16]'} mb-4 shadow-lg border border-border bg-black flex items-center justify-center`}>
                          {video.videoUrl ? (
                            <video 
                              src={`${video.videoUrl}#t=0.001`} 
                              className={`w-full h-full object-cover bg-black ${video.videoClass || ''}`}
                              controls
                              preload="metadata"
                              poster={video.thumbnail}
                            />
                          ) : (
                            <>
                              <img 
                                src={video.thumbnail} 
                                alt={video.title} 
                                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105 cursor-pointer"
                                loading="lazy"
                                decoding="async"
                              />
                              <div className="absolute inset-0 bg-black/40 group-hover:bg-black/20 transition-colors flex items-center justify-center pointer-events-none">
                                <div className="w-16 h-16 rounded-full bg-accent/90 flex items-center justify-center text-white shadow-lg transform group-hover:scale-110 transition-transform">
                                  <Play className="w-6 h-6 ml-1 fill-current" />
                                </div>
                              </div>
                            </>
                          )}
                        </div>
                        <h3 className="text-xl font-bold text-primary mb-1">{video.title}</h3>
                        <div className="flex items-center justify-between text-sm">
                          <span className="font-medium text-foreground">{video.name}</span>
                          <span className="text-accent">{video.service}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </TabsContent>
              )})}
            </Tabs>
          </div>
        </section>

        {/* The Widget Section */}
        <div className="py-8">
            <ReviewWidget />
        </div>

        {/* CTA Section */}
        <section className="py-24 bg-secondary/30">
          <div className="container mx-auto px-4 text-center max-w-3xl">
            <h2 className="text-3xl md:text-4xl font-bold text-primary mb-6">Ready to Write Your Own Success Story?</h2>
            <p className="text-lg text-muted-foreground mb-8">
              Don't let pain hold you back any longer. Schedule a consultation today and let us create a personalized treatment plan for you.
            </p>
            <Button size="lg" className="bg-accent text-white hover:bg-accent/90 border-0" asChild>
              <Link to="/appointment-scheduling">
                Book Your Appointment <ChevronRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </div>
        </section>

      </main>
      <Footer />
    </div>
  );
}
